import {
  Autocomplete,
  Button,
  Chip,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import React, { useState } from "react";
import CDateTimePicker from "../../../basecomp/components/CDateTimePicker";
import CBackDrop from "../../../basecomp/components/CBackDrop";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import { useTranslation } from "react-i18next";
import InventoryVM from "../viewmodel/InventoryVM";
import { Box, Stack } from "@mui/system";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";

const InventoryView = () => {
  const {
    downloadExportAllFile,
    handleStartDateChange,
    handleEndDateChange,
    downloadMaterialExportAllFile,
    handleMaterialStartDateChange,
    handleMaterialEndDateChange,
    downloadStickerExportAllFile,
    handleStickerStartDateChange,
    handleStickerEndDateChange,
    loading,
    setSelectedLocations,
    selectedLocations,
    sLocOptions,
    materialNo,
    printedBy,
    putAwayBy,
    setMaterialNo,
    setPrintedBy,
    setPutAwayBy,
    printedOptions,
    putawayOptions,
    isPutawayPending,
    setIsPutawayPending,
  } = InventoryVM();

  const { t } = useTranslation();
  const theme = useTheme();
  const isXs = useMediaQuery(theme.breakpoints.down("sm"));

  const cardStyle = {
    p: 4,
    borderRadius: 3,
    boxShadow: 3,
    bgcolor: "background.paper",
    minHeight: 300,
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
  };

  return (
    <div
      style={{
        marginTop: "6em",
        padding: "1rem",
      }}
    >
      <CBackDrop cOpen={loading} />

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "1fr",
            md: "repeat(2, 1fr)",
          },
          gap: 3,
          maxWidth: "1700px",
          mx: "auto",
        }}
      >
        {/* Inventory Counting Report */}
        <Box sx={cardStyle}>
          <Typography
            variant="h6"
            sx={{
              textAlign: "center",
              color: "#3CB043",
              fontWeight: 600,
              mb: 4,
            }}
          >
            Inventory Counting Report
          </Typography>

          <Box
            sx={{
              display: "flex",
              gap: 2,
              alignItems: "flex-start",
              flexDirection: isXs ? "column" : "row",
            }}
          >
            {/* Storage Location */}
            <Box sx={{ flex: 2, width: "100%" }}>
              <Autocomplete
                multiple
                options={sLocOptions}
                value={selectedLocations}
                filterSelectedOptions
                onChange={(event, newValue) => {
                  setSelectedLocations(newValue);
                }}
                renderTags={() => null}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Storage Locations"
                    placeholder="Select locations"
                    size="small"
                  />
                )}
              />

              {selectedLocations.length > 0 && (
                <Stack
                  direction="row"
                  spacing={1}
                  useFlexGap
                  flexWrap="wrap"
                  sx={{ mt: 1 }}
                >
                  {selectedLocations.map((location) => (
                    <Chip
                      key={location}
                      label={location}
                      size="small"
                      onDelete={() =>
                        setSelectedLocations(
                          selectedLocations.filter(
                            (item) => item !== location
                          )
                        )
                      }
                    />
                  ))}
                </Stack>
              )}
            </Box>

            {/* Start Date */}
            <Box sx={{ flex: 1, width: "100%" }}>
              <CDateTimePicker
                cHelperText={t("start_date")}
                cOnChange={handleStartDateChange}
                cFullWidth={true}
                hideTime={true}
              />
            </Box>

            {/* End Date */}
            <Box sx={{ flex: 1, width: "100%" }}>
              <CDateTimePicker
                cHelperText={t("end_date")}
                cOnChange={handleEndDateChange}
                cFullWidth={true}
                hideTime={true}
              />
            </Box>
          </Box>

          <Box
            sx={{
              mt: 4,
              display: "flex",
              justifyContent: "center",
            }}
          >
            <Button
              variant="contained"
              startIcon={<FileDownloadIcon />}
              onClick={downloadExportAllFile}
              sx={{
                borderRadius: 2,
                px: 4,
                minWidth: 200,
              }}
            >
              Download Report
            </Button>
          </Box>
        </Box>

        {/* Material Counting Data */}
        <Box sx={cardStyle}>
          <Typography
            variant="h6"
            sx={{
              textAlign: "center",
              color: "#3CB043",
              fontWeight: 600,
              mb: 4,
            }}
          >
            Material Counting Data
          </Typography>

          <Box
            sx={{
              display: "flex",
              gap: 2,
              alignItems: "center",
              flexDirection: isXs ? "column" : "row",
            }}
          >
            <Box sx={{ flex: 1, width: "100%" }}>
              <CDateTimePicker
                cHelperText={t("start_date")}
                cOnChange={handleMaterialStartDateChange}
                cFullWidth={true}
                hideTime={true}
              />
            </Box>

            <Box sx={{ flex: 1, width: "100%" }}>
              <CDateTimePicker
                cHelperText={t("end_date")}
                cOnChange={handleMaterialEndDateChange}
                cFullWidth={true}
                hideTime={true}
              />
            </Box>
          </Box>

          <Box
            sx={{
              mt: 4,
              display: "flex",
              justifyContent: "center",
            }}
          >
            <Button
              variant="contained"
              startIcon={<FileDownloadIcon />}
              onClick={downloadMaterialExportAllFile}
              sx={{
                borderRadius: 2,
                px: 4,
                minWidth: 200,
              }}
            >
              Download Report
            </Button>
          </Box>
        </Box>

        {/* Sticker Printing Report */}
        <Box sx={cardStyle}>
          <Typography
            variant="h6"
            sx={{
              textAlign: "center",
              color: "#3CB043",
              fontWeight: 600,
              mb: 4,
            }}
          >
            Sticker Printing Report
          </Typography>

          <Box
            sx={{
              display: "flex",
              gap: 2,
              alignItems: "center",
              flexDirection: isXs ? "column" : "row",
            }}
          >
            <Box sx={{ flex: 1, width: "100%" }}>
              <CDateTimePicker
                cHelperText={t("start_date")}
                cOnChange={handleStickerStartDateChange}
                cFullWidth={true}
                hideTime={true}
              />
            </Box>

            <Box sx={{ flex: 1, width: "100%" }}>
              <CDateTimePicker
                cHelperText={t("end_date")}
                cOnChange={handleStickerEndDateChange}
                cFullWidth={true}
                hideTime={true}
              />
            </Box>
          </Box>

          <Box
            sx={{
              display: "flex",
              gap: 2,
              alignItems: "center",
              mt: 3,
              flexDirection: isXs ? "column" : "row",
            }}
          >
            <TextField
              label="Material No"
              value={materialNo}
              onChange={(e) => setMaterialNo(e.target.value)}
              sx={{ flex: 1, width: "100%" }}
            />

            <Autocomplete
              options={putawayOptions}
              value={putAwayBy}
              onChange={(_, newValue) => setPutAwayBy(newValue||"")}
              sx={{ flex: 1, width: "100%" }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Put Away By "
                  placeholder="Select Name"
                />
              )}
            />

            <Autocomplete
              options={printedOptions}
              value={printedBy}
              onChange={(_, newValue) => setPrintedBy(newValue||"")}
              sx={{ flex: 1, width: "100%" }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Printed By"
                  placeholder="Select Name"
                />
              )}
            />
          </Box>

          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-start",
              mt: 1,
            }}
          >
            <FormControlLabel
              control={
                <Checkbox
                  checked={isPutawayPending}
                  onChange={(e) => setIsPutawayPending(e.target.checked)}
                />
              }
              label="Pending Put Aways"
            />
          </Box>

          <Box
            sx={{
              mt: 4,
              display: "flex",
              justifyContent: "center",
            }}
          >
            <Button
              variant="contained"
              startIcon={<FileDownloadIcon />}
              onClick={downloadStickerExportAllFile}
              sx={{
                borderRadius: 2,
                px: 4,
                minWidth: 200,
              }}
            >
              Download Report
            </Button>
          </Box>
        </Box>
      </Box>
    </div>
  );
};

export default InventoryView;